import { DateTime, DateTimeFormatOptions, Duration } from 'luxon';

const DAY_MONTH_FORMAT: DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
};

const DATE_FORMAT: DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const DATE_WEEK_FORMAT: DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

const TIME_FORMAT: DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};

export const USER_LOCAL_TIMEZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getYYYYMMDDTimeStamp = (timestamp: Date) => {
  return DateTime.fromJSDate(timestamp).toFormat('yyyy-MM-dd');
};

export const getFullDateTimeStamp = (timestamp: string) => {
  return getTimeStamp(timestamp, true, false, true, true);
};

export const getShortDate = (timestamp: string) => {
  return DateTime.fromISO(timestamp)
    .setZone(USER_LOCAL_TIMEZONE)
    .toLocaleString(DATE_FORMAT);
};

export const getTimeStamp = (
  timestamp: string,
  includesTime = true,
  relativeTime = false,
  includesYear = true,
  includesWeek = false,
) => {
  const dateTime = DateTime.fromISO(timestamp).setZone(USER_LOCAL_TIMEZONE);

  if (relativeTime) {
    const rawRelativeDate = dateTime
      .toRelative({ style: 'long' })
      ?.split('.')
      .join('');

    return rawRelativeDate ?? 'N/A';
  }

  const timeStampFormat = includesTime
    ? { ...DATE_FORMAT, ...TIME_FORMAT }
    : includesYear
    ? DATE_FORMAT
    : includesWeek
    ? DATE_WEEK_FORMAT
    : DAY_MONTH_FORMAT;

  return dateTime.toLocaleString(timeStampFormat);
};

export const getDateWithStartAndEndTime = (
  date: string,
  startTime: string,
  endTime: string,
) => {
  const dateTime = DateTime.fromISO(date).setZone(USER_LOCAL_TIMEZONE);
  const formattedDate = dateTime.toLocaleString(DATE_FORMAT);

  const formattedStartTime = formatTime(startTime);
  const formattedEndTime = formatTime(endTime);

  return `${formattedDate} ${formattedStartTime} - ${formattedEndTime}`;
};

export const getWeekdayDate = (timestamp: string, showYear?: boolean) => {
  return DateTime.fromISO(timestamp)
    .setZone(USER_LOCAL_TIMEZONE)
    .toLocaleString({
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(showYear && { year: 'numeric' }),
    });
};

export const DEFAULT_MAIL_TIMEZONE = 'America/Los_Angeles';

/** Default when provider has not set a timezone in Settings (Pacific). */
export const DEFAULT_PROVIDER_TIMEZONE = DEFAULT_MAIL_TIMEZONE;

const UTC_LIKE_FOR_PROVIDER = new Set(['UTC', 'Etc/UTC', 'GMT', 'Etc/GMT']);

function isResolvableIanaTimezone(tz: string): boolean {
  const trimmed = tz.trim();
  if (!trimmed) return false;
  return DateTime.now().setZone(trimmed).isValid;
}

/** Valid provider/patient timezone from DB; UTC placeholders are treated as unset. */
function isValidProviderTimezone(tz: string): boolean {
  const trimmed = tz.trim();
  if (!trimmed || UTC_LIKE_FOR_PROVIDER.has(trimmed)) return false;
  return isResolvableIanaTimezone(trimmed);
}

/**
 * Provider timezone from Settings, or the viewer's local timezone when unset / UTC placeholder.
 */
export function getProviderSettingsTimezone(
  providerTimezone?: string | null,
): string {
  if (providerTimezone && isValidProviderTimezone(providerTimezone)) {
    return providerTimezone.trim();
  }
  if (USER_LOCAL_TIMEZONE && isResolvableIanaTimezone(USER_LOCAL_TIMEZONE)) {
    return USER_LOCAL_TIMEZONE;
  }
  return DEFAULT_PROVIDER_TIMEZONE;
}

/**
 * Parse API timestamps stored as naive UTC (with or without a Z suffix).
 */
export function parseUtcIso(timestamp: string): DateTime {
  const trimmed = timestamp.trim();
  if (!trimmed) {
    return DateTime.invalid('empty timestamp');
  }
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const parsed = DateTime.fromISO(trimmed, { setZone: true });
    return parsed.isValid ? parsed.toUTC() : parsed;
  }
  return DateTime.fromISO(trimmed, { zone: 'utc' });
}

export function getTimezoneAbbreviation(
  timezone: string,
  fallback = timezone,
): string {
  try {
    const name = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;
    return name || fallback;
  } catch {
    return fallback;
  }
}

function isMeaningfulTimezone(tz: string): boolean {
  return isValidProviderTimezone(tz);
}

/**
 * Practice timezone for scheduling UI: provider Settings value, else Pacific.
 */
export function getPracticeTimezoneForScheduling(
  providerTimezone?: string | null,
): string {
  if (providerTimezone && isMeaningfulTimezone(providerTimezone)) {
    return providerTimezone.trim();
  }
  return DEFAULT_PROVIDER_TIMEZONE;
}

export function hasProviderTimezoneSetting(
  providerTimezone?: string | null,
): boolean {
  return Boolean(providerTimezone && isMeaningfulTimezone(providerTimezone));
}

/** Label for Schedule Mail default send time (practice TZ abbr, or PST when unset). */
export function getScheduleMailSendTimeLabel(
  providerTimezone?: string | null,
): string {
  const base = 'Select a time each Mail will be delivered';
  if (hasProviderTimezoneSetting(providerTimezone)) {
    return `${base} (${getTimezoneAbbreviation(providerTimezone!.trim())})`;
  }
  return `${base} (PST)`;
}

/**
 * Resolve timezone for Mail display.
 * With patientTimezone: patient > provider > viewer local > PST (patient portal).
 * Without patientTimezone: provider > viewer local > PST (provider portal).
 * Treats UTC as unset (common DB default) so we fall through.
 */
export function getMailDisplayTimezone(
  patientTimezone?: string | null,
  providerTimezone?: string | null,
): string {
  if (patientTimezone && isMeaningfulTimezone(patientTimezone))
    return patientTimezone.trim();
  if (providerTimezone && isMeaningfulTimezone(providerTimezone))
    return providerTimezone.trim();
  if (USER_LOCAL_TIMEZONE && isMeaningfulTimezone(USER_LOCAL_TIMEZONE))
    return USER_LOCAL_TIMEZONE;
  return DEFAULT_MAIL_TIMEZONE;
}

const MAIL_DATE_FORMAT: DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

const MAIL_TIME_ONLY_FORMAT: DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

const formatMailDateTime = (dt: DateTime) =>
  `${dt.toLocaleString(MAIL_DATE_FORMAT)} at ${dt.toLocaleString(
    MAIL_TIME_ONLY_FORMAT,
  )}`;

/**
 * Format Mail sent timestamp. Pass patientTimezone for patient portal (patient > provider > viewer > PST),
 * omit for provider portal (provider > viewer > PST).
 */
export function formatMailSentAt(
  timestamp: string,
  patientTimezone?: string | null,
  providerTimezone?: string | null,
): string {
  const tz = getMailDisplayTimezone(patientTimezone, providerTimezone);
  return formatMailDateTime(DateTime.fromISO(timestamp).setZone(tz));
}

/**
 * Format a scheduled mail calendar date + wall-clock send time (e.g. "Jul 23, 2026 at 9:00 AM").
 * Does not apply timezone conversion — scheduledDate/sendTime are already in delivery local time.
 */
export function formatScheduledMailAt(
  scheduledDate: string,
  sendTime?: string | null,
): string {
  const datePart = scheduledDate.slice(0, 10);
  const timePart = (sendTime?.trim() || '09:00').slice(0, 5);
  const dt = DateTime.fromISO(`${datePart}T${timePart}:00`);
  if (!dt.isValid) {
    return scheduledDate;
  }
  return `${dt.toLocaleString(DATE_FORMAT)} at ${dt.toLocaleString(
    TIME_FORMAT,
  )}`;
}

/**
 * Format Mail edited timestamp for display.
 * Same day as sentAt: just time. Different day: date and time.
 */
export function formatMailEditedAt(
  editedAt: string,
  sentAt: string,
  patientTimezone?: string | null,
  providerTimezone?: string | null,
): string {
  const tz = getMailDisplayTimezone(patientTimezone, providerTimezone);
  const editedDt = DateTime.fromISO(editedAt).setZone(tz);
  const sentDt = DateTime.fromISO(sentAt).setZone(tz);
  const sameDay =
    editedDt.hasSame(sentDt, 'day') &&
    editedDt.hasSame(sentDt, 'month') &&
    editedDt.hasSame(sentDt, 'year');
  if (sameDay) {
    return editedDt.toLocaleString(MAIL_TIME_ONLY_FORMAT);
  }
  return formatMailDateTime(editedDt);
}

export const getDurationMSS = (seconds?: number | null) => {
  if (!seconds) seconds = 0;
  return Duration.fromMillis(seconds * 1000).toFormat('m:ss');
};

export const getDurationSS = (seconds = 0) => {
  return Duration.fromMillis(seconds * 1000).toFormat('ss');
};

export const getRelativeTimestamp = (timestamp: string) =>
  DateTime.fromISO(timestamp).toRelative({ style: 'short' })?.replace('.', '');

export const isAfterDate = (date: Date, afterDate = new Date()) =>
  DateTime.fromJSDate(date) > DateTime.fromJSDate(afterDate);

export const daysUntil = (untilTimestamp: string) => {
  const now = DateTime.now();
  const untilDate = DateTime.fromISO(untilTimestamp);

  return Math.ceil(untilDate.diff(now, 'days').days);
};

export function formatCurrentDate(): string {
  const today = new Date();

  return today.toLocaleDateString('en-us', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export const formatDuration = (seconds: number): string => {
  if (!seconds) return '0s';

  const duration = Duration.fromMillis(seconds * 1000)
    .shiftTo('hours', 'minutes', 'seconds')
    .normalize();

  const parts = [];

  if (duration.hours > 0) {
    parts.push(`${Math.floor(duration.hours)}h`);
  }
  if (duration.minutes > 0) {
    parts.push(`${Math.floor(duration.minutes)}m`);
  }
  if (duration.seconds > 0 || parts.length === 0) {
    parts.push(`${Math.round(duration.seconds)}s`);
  }

  return parts.join(' ');
};

export const formatTime = (timestamp: string) => {
  return DateTime.fromISO(timestamp)
    .setZone(USER_LOCAL_TIMEZONE)
    .toLocaleString({
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
};

/**
 * Formats a time range from start and end ISO timestamps
 * Returns a formatted string like "9:00 AM - 10:30 AM"
 * Falls back to raw timestamps if invalid
 */
export const formatTimeRange = (startTime: string, endTime: string): string => {
  const startDt = DateTime.fromISO(startTime);
  const endDt = DateTime.fromISO(endTime);

  const startFormatted = startDt.isValid
    ? startDt.setZone(USER_LOCAL_TIMEZONE).toLocaleString({
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : startTime;

  const endFormatted = endDt.isValid
    ? endDt.setZone(USER_LOCAL_TIMEZONE).toLocaleString({
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : endTime;

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Formats a date string to YYYY-MM-DD format for HTML date inputs
 */
export const formatDateForInput = (dateString: string): string => {
  return new Date(dateString).toISOString().split('T')[0];
};

/**
 * Formats a date string to HH:mm format for HTML time inputs
 */
export const formatTimeForInput = (dateString: string): string => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
};
